/**
 * Contact — formulaire public.
 *
 * Poste vers POST /api/contact/send. Aucune auth requise.
 * L'affichage est HONNÊTE : la réponse indique si l'email a été effectivement
 * envoyé (email_sent: true) OU seulement persisté (email_sent: false, faute
 * d'intégration Resend configurée) — dans les 2 cas, l'utilisateur reçoit une
 * confirmation "message bien reçu" et voit également l'email direct de support.
 */
import { useState } from "react";
import { CheckCircle2, Loader2, Mail, AlertTriangle } from "lucide-react";
import LegalShell from "@/components/LegalShell";
import { API } from "@/lib/api";

const SUPPORT_EMAIL = "support@openmeta.fr";

export default function Contact() {
    const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", website: "" });
    const [status, setStatus] = useState("idle"); // idle | sending | success | error
    const [errorMsg, setErrorMsg] = useState("");

    function updateField(k, v) {
        setForm((f) => ({ ...f, [k]: v }));
    }

    async function submit(e) {
        e.preventDefault();
        setStatus("sending");
        setErrorMsg("");
        try {
            const res = await fetch(`${API}/contact/send`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(form),
            });
            if (!res.ok) {
                const d = await res.json().catch(() => ({}));
                setStatus("error");
                setErrorMsg(d.detail || "Erreur inconnue.");
                return;
            }
            setStatus("success");
        } catch (err) {
            setStatus("error");
            setErrorMsg("Impossible de contacter le serveur.");
        }
    }

    return (
        <LegalShell
            testid="page-contact"
            eyebrow="Contact"
            title="Une question, un retour, une demande ?"
            subtitle="Nous répondons personnellement à chaque message, généralement sous 48 heures."
        >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="rounded-2xl border border-lexiBorder bg-paperAlt p-5 md:col-span-1" data-testid="contact-side">
                    <p className="text-[11px] tracking-[0.24em] uppercase text-inkSoft">Nous écrire directement</p>
                    <div className="mt-3 flex items-start gap-2">
                        <Mail className="w-4 h-4 mt-0.5 text-terracotta" strokeWidth={1.75} />
                        <a
                            href={`mailto:${SUPPORT_EMAIL}`}
                            className="font-serif text-lg text-ink hover:text-terracotta transition-colors break-all"
                            data-testid="contact-direct-email"
                        >
                            {SUPPORT_EMAIL}
                        </a>
                    </div>
                    <p className="mt-4 text-[13px] text-inkSoft leading-relaxed">
                        Pour une demande RGPD (accès, rectification, suppression), écrivez depuis l&apos;adresse email
                        associée à votre compte. Voir aussi la{" "}
                        <a href="/confidentialite" className="text-terracotta hover:underline">politique de confidentialité</a>.
                    </p>
                </div>

                <div className="md:col-span-2">
                    {status === "success" ? (
                        <div
                            className="rounded-2xl border border-emerald bg-emerald/10 p-6 flex items-start gap-3"
                            data-testid="contact-success"
                        >
                            <CheckCircle2 className="w-5 h-5 text-emerald mt-0.5 shrink-0" strokeWidth={2} />
                            <div>
                                <p className="font-serif text-lg text-ink">Message bien reçu.</p>
                                <p className="text-sm text-inkSoft mt-1">
                                    Nous vous répondrons à l&apos;adresse indiquée. En cas d&apos;urgence, vous pouvez
                                    aussi écrire directement à{" "}
                                    <a href={`mailto:${SUPPORT_EMAIL}`} className="text-terracotta hover:underline">{SUPPORT_EMAIL}</a>.
                                </p>
                                <button
                                    onClick={() => {
                                        setStatus("idle");
                                        setForm({ name: "", email: "", subject: "", message: "", website: "" });
                                    }}
                                    className="mt-4 text-sm text-terracotta hover:underline"
                                    data-testid="contact-new-message"
                                >
                                    Envoyer un autre message →
                                </button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={submit} className="space-y-4" data-testid="contact-form" noValidate>
                            {/* Honeypot */}
                            <input
                                type="text"
                                name="website"
                                value={form.website}
                                onChange={(e) => updateField("website", e.target.value)}
                                tabIndex={-1}
                                autoComplete="off"
                                aria-hidden="true"
                                style={{ position: "absolute", left: "-5000px", opacity: 0, height: 0, width: 0 }}
                            />

                            <div>
                                <label htmlFor="contact-name" className="block text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-1">
                                    Nom
                                </label>
                                <input
                                    id="contact-name"
                                    data-testid="contact-name"
                                    type="text"
                                    required
                                    maxLength={120}
                                    value={form.name}
                                    onChange={(e) => updateField("name", e.target.value)}
                                    className="w-full bg-white border border-lexiBorder focus:border-terracotta focus:outline-none rounded-xl px-4 py-2.5 text-sm"
                                    placeholder="Camille Dupont"
                                />
                            </div>

                            <div>
                                <label htmlFor="contact-email" className="block text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-1">
                                    Email
                                </label>
                                <input
                                    id="contact-email"
                                    data-testid="contact-email"
                                    type="email"
                                    required
                                    value={form.email}
                                    onChange={(e) => updateField("email", e.target.value)}
                                    className="w-full bg-white border border-lexiBorder focus:border-terracotta focus:outline-none rounded-xl px-4 py-2.5 text-sm"
                                    placeholder="camille@exemple.fr"
                                />
                            </div>

                            <div>
                                <label htmlFor="contact-subject" className="block text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-1">
                                    Sujet
                                </label>
                                <input
                                    id="contact-subject"
                                    data-testid="contact-subject"
                                    type="text"
                                    required
                                    maxLength={160}
                                    value={form.subject}
                                    onChange={(e) => updateField("subject", e.target.value)}
                                    className="w-full bg-white border border-lexiBorder focus:border-terracotta focus:outline-none rounded-xl px-4 py-2.5 text-sm"
                                    placeholder="Question sur les révisions"
                                />
                            </div>

                            <div>
                                <label htmlFor="contact-message" className="block text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-1">
                                    Message
                                </label>
                                <textarea
                                    id="contact-message"
                                    data-testid="contact-message"
                                    required
                                    minLength={10}
                                    maxLength={5000}
                                    rows={7}
                                    value={form.message}
                                    onChange={(e) => updateField("message", e.target.value)}
                                    className="w-full bg-white border border-lexiBorder focus:border-terracotta focus:outline-none rounded-xl px-4 py-3 text-sm resize-y"
                                    placeholder="Dites-nous en plus…"
                                />
                                <p className="text-[10px] text-inkSoft mt-1 text-right">
                                    {form.message.length} / 5000
                                </p>
                            </div>

                            {status === "error" && (
                                <div
                                    className="rounded-xl border border-terracotta bg-terracotta/8 px-4 py-3 flex items-start gap-2 text-sm text-ink"
                                    data-testid="contact-error"
                                >
                                    <AlertTriangle className="w-4 h-4 text-terracotta mt-0.5 shrink-0" />
                                    <span>{errorMsg || "Erreur inconnue — réessayez, ou écrivez directement à support."}</span>
                                </div>
                            )}

                            <div className="pt-1">
                                <button
                                    type="submit"
                                    disabled={status === "sending"}
                                    data-testid="contact-submit"
                                    className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60 transition-colors"
                                >
                                    {status === "sending" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                    {status === "sending" ? "Envoi…" : "Envoyer le message"}
                                </button>
                                <p className="text-[11px] text-inkSoft mt-3">
                                    En envoyant ce message, vous acceptez que Lexia School conserve son contenu à des fins
                                    de suivi de votre demande. Voir la{" "}
                                    <a href="/confidentialite" className="text-terracotta hover:underline">politique de confidentialité</a>.
                                </p>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        </LegalShell>
    );
}
